/**
 * useEventFormatters Hook
 *
 * Shared formatting utilities for timeline event components.
 * Provides access to ViewStateLookupsContext and common formatters.
 *
 * Usage:
 * ```typescript
 * function PropertyUpdatedEvent({ event }: EventComponentProps) {
 *   const { getActorInfo, formatPropertyValue, getPropertyLabel } = useEventFormatters();
 *   const actor = getActorInfo(event);
 *   // ...
 * }
 * ```
 */

import { formatFallback } from '@/lib/crm/utils/labelFormatters';
import { MEETING_NOTES_SOURCE_LABELS } from '@/lib/crm/utils/meetingNotes';
import { unwrapPropertyValue } from '@/types/crm';

import { useViewStateLookups } from './context/TimelineLookupsContext';
import type {
  HydratedEntity,
  LinkedInMessagePayload,
  TimelineEvent,
} from './types';
import { EVENT_TYPES } from './types';

// Email events whose ahoy_entity source (the ingesting mailbox's integration
// entity) may be unreadable to the viewer; their actor is resolved via the
// mailbox owner's user id carried in event metadata.
const EMAIL_ACTOR_EVENT_TYPES = new Set<string>([
  EVENT_TYPES.EMAIL_SENT,
  EVENT_TYPES.EMAIL_RECEIVED,
]);

/**
 * Actor information extracted from event source
 */
export interface ActorInfo {
  /** Display name (e.g., "You", "John Doe", "Ahoy", "Gmail") */
  name: string;
  /** Source type from backend */
  source_type_name: string;
  /** Source ID (user ID, message ID, etc.) */
  source_id: string;
  /** Whether the actor is the currently logged-in user */
  isCurrentUser: boolean;
  /** Entity when the actor is an entity (e.g. automations) */
  object?: HydratedEntity;
}

/**
 * Hook providing shared formatting utilities for event components.
 *
 * Wraps ViewStateLookupsContext and provides:
 * - getActorInfo: Extract actor display info from event
 * - formatPropertyValue: Format property values for display
 * - formatPropertyName: Format property names for display
 * - getObjectLabel: Get label for associated object by entity ID
 * - getUserDisplayName: Get user display name by user ID
 */
export function useEventFormatters() {
  const {
    lookups,
    getUserDisplayName,
    getObjectLabel,
    getPropertyLabel,
    currentUserId,
  } = useViewStateLookups();

  /**
   * Extract actor information from event
   *
   * Maps backend source_type_name to display-friendly actor info.
   * Shows "You" when the actor is the currently logged-in user.
   */
  const getActorInfo = (event: TimelineEvent): ActorInfo => {
    const { source_type_name, source_id } = event;

    // Source types that reference a user ID in source_id
    const isUserSource =
      source_type_name === 'ahoy_user' ||
      source_type_name === 'ahoy_chrome_extension' ||
      source_type_name === 'ahoy_linkedin_extension';

    const isCurrentUser = !!(
      isUserSource &&
      source_id &&
      currentUserId &&
      source_id === currentUserId
    );

    let name: string;
    let actorObject: HydratedEntity | undefined;
    switch (source_type_name) {
      case 'ahoy_user':
        if (isCurrentUser) {
          name = 'You';
        } else if (source_id) {
          name = getUserDisplayName(source_id);
        } else {
          name = 'User';
        }
        break;

      case 'ahoy_action':
        name = 'Ahoy';
        break;

      case 'ahoy_gmail':
      case 'ahoy_gmail_message':
        name = 'Gmail';
        break;

      case 'ahoy_gcal_sync':
        name = 'Google Calendar';
        break;

      case 'ahoy_quo_sync':
        name = 'Quo';
        break;

      case 'ahoy_stripe_sync':
        name = 'Stripe';
        break;

      case 'ahoy_hubspot_sync':
        name = 'HubSpot';
        break;

      case 'ahoy_salesforce_sync':
        name = 'Salesforce';
        break;

      case 'ahoy_meeting_notes_sync': {
        const notesSource = event.metadata?.notes_source;
        if (
          typeof notesSource === 'string' &&
          notesSource in MEETING_NOTES_SOURCE_LABELS
        ) {
          name = MEETING_NOTES_SOURCE_LABELS[notesSource];
        } else {
          name = 'Meeting Summaries';
        }
        break;
      }

      case 'ahoy_system':
      case 'ahoy_timeline_event':
      case 'ahoy_proposal':
      case 'ahoy_user_agent_conversation':
        name = 'Ahoy';
        break;

      case 'ahoy_fullenrich':
        name = 'Ahoy';
        break;

      case 'ahoy_harbour':
        name = 'Harbour';
        break;

      case 'ahoy_waitlist_entry':
        name = 'Ahoy Waitlist';
        break;

      case 'ahoy_inbound_webhook':
        // Use source_id to determine the integration name (e.g., 'kondo' -> 'Kondo').
        // Some source ids need an explicit label because simple capitalization would
        // read wrong (e.g. 'linkedin_extension' should show as 'LinkedIn', not
        // 'Linkedin_extension').
        if (source_id === 'linkedin_extension') {
          name = 'LinkedIn';
        } else if (source_id) {
          name = source_id.charAt(0).toUpperCase() + source_id.slice(1);
        } else {
          name = 'Webhook';
        }
        break;

      case 'ahoy_chrome_extension':
      case 'ahoy_linkedin_extension': // backwards compat during migration rollout
        if (isCurrentUser) {
          name = 'You';
        } else if (source_id) {
          name = getUserDisplayName(source_id);
        } else {
          name = 'Chrome Extension';
        }
        break;

      case 'ahoy_entity': {
        actorObject = source_id ? lookups.entities[source_id] : undefined;
        if (actorObject && source_id) {
          name = getObjectLabel(source_id) || 'Object';
          break;
        }
        // Synced email events are sourced by the ingesting mailbox's integration
        // entity, which is readable only by its owner — for other viewers it
        // never hydrates. The event's metadata carries the mailbox owner's user
        // id (stamped at ingest); resolve the owner through lookups.users so
        // the card reads "<owner> received email" with the same clickable badge
        // a user-entity source gets.
        if (EMAIL_ACTOR_EVENT_TYPES.has(event.event_type_name)) {
          const ownerId = event.metadata?.gmail_user_id;
          const ownerEntityId =
            ownerId === undefined || ownerId === null
              ? undefined
              : lookups.users[String(ownerId)];
          const ownerEntity = ownerEntityId
            ? lookups.entities[ownerEntityId]
            : undefined;
          if (ownerEntity) {
            actorObject = ownerEntity;
            name = ownerEntity.ahoy_display_name ?? 'User';
            break;
          }
          // Honest fallback: a mailbox sync ingested this, not an automation.
          name = 'Synced mailbox';
          break;
        }
        name = 'Automation';
        break;
      }

      case 'ahoy_linkedin_message': {
        const linkedInPayload = event.payload as
          | LinkedInMessagePayload
          | undefined;
        const isSent =
          event.event_type_name === EVENT_TYPES.LINKEDIN_MESSAGE_SENT;
        const userSideEntityId = isSent
          ? linkedInPayload?.from
          : linkedInPayload?.to;
        const userSideEntity = userSideEntityId
          ? lookups.entities[userSideEntityId]
          : undefined;
        const participantName = userSideEntity?.properties?.ahoy_display_name
          ?.value as string | undefined;
        // Without a current user we cannot tell whether the user-side entity is
        // "You", so we must not attribute the event to a specific participant —
        // fall back to the neutral "LinkedIn" label (also covers loading and
        // anonymous states).
        if (
          !userSideEntityId ||
          !userSideEntity ||
          !participantName ||
          !currentUserId
        ) {
          name = 'LinkedIn';
          break;
        }
        const currentUserEntityId = lookups.users[String(currentUserId)];
        if (currentUserEntityId && currentUserEntityId === userSideEntityId) {
          name = 'You';
        } else {
          // The badge (ActorName) renders the top-level `ahoy_display_name`, but
          // the resolved participant name lives under
          // `properties.ahoy_display_name.value` — the backend leaves the
          // top-level as a placeholder for property-less entities. Copy the
          // resolved name across so the badge shows it instead of "Unknown".
          actorObject = {
            ...userSideEntity,
            ahoy_display_name: participantName,
          };
          name = participantName;
        }
        break;
      }

      default:
        name = 'Unknown';
    }

    return {
      name,
      source_type_name: source_type_name || 'unknown',
      source_id: source_id || '',
      isCurrentUser,
      object: actorObject,
    };
  };

  /**
   * Format property name for display
   *
   * First tries to get label from lookups, falls back to formatting the name.
   * Transforms: "ahoy_first_name" → "First name"
   */
  const formatPropertyName = (
    property_type_name: string,
    entityType?: string
  ): string => {
    // Try to get label from lookups if entityType is provided
    if (entityType) {
      const label = getPropertyLabel(entityType, property_type_name);
      if (label !== property_type_name) {
        return label;
      }
    }

    // Fall back to the shared formatter: strip prefix, sentence case
    return formatFallback(property_type_name);
  };

  /**
   * Format property value for display
   *
   * Handles backend value abstraction:
   * - Simple types: { value: primitive }
   * - pipeline_stage: { value: { pipeline_name, stage_name } }
   * - quantity: { value: { amount, unit } }
   */
  const formatPropertyValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '—';
    }

    // timeline event payload: single-level {value} wrapper — unwrap once via unwrapPropertyValue, then recurse for formatting
    if (typeof value === 'object' && value !== null) {
      const unwrapped = unwrapPropertyValue(value);
      if (unwrapped !== value) {
        return formatPropertyValue(unwrapped);
      }
    }

    // Handle complex types
    if (typeof value === 'object' && value !== null) {
      // pipeline_stage
      if ('pipeline_name' in value && 'stage_name' in value) {
        const stage = value as { stage_name: string };
        return stage.stage_name;
      }

      // quantity
      if ('amount' in value && 'unit' in value) {
        const qty = value as { amount: string | number; unit: string };
        return `${qty.amount} ${qty.unit}`;
      }

      // entity reference (association)
      if ('entity_id' in value && 'label' in value) {
        return (value as { label: string }).label;
      }

      // Array
      if (Array.isArray(value)) {
        return value.map(v => formatPropertyValue(v)).join(', ');
      }

      // Last resort: stringify
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    // Truncate long strings
    const str = String(value);
    return str.length > 100 ? `${str.substring(0, 97)}...` : str;
  };

  /**
   * Format association type for display
   *
   * Transforms: "ahoy_contact_to_company" → "Company"
   */
  const formatAssociationType = (association_type_name: string): string => {
    const last =
      association_type_name
        .replace(/^ahoy_/, '')
        .split('_to_')
        .pop() ?? '';
    return last.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  /**
   * Format stage name for display
   *
   * Transforms: "ahoy_qualification" → "Qualification"
   */
  const formatStageName = (stage_name: string): string => {
    return stage_name
      .replace(/^ahoy_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  return {
    // Context data
    lookups,

    // Lookup helpers
    getUserDisplayName,
    getObjectLabel,
    getPropertyLabel,

    // Event helpers
    getActorInfo,

    // Formatting helpers
    formatPropertyName,
    formatPropertyValue,
    formatAssociationType,
    formatStageName,
  };
}
